import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Truck, LayoutList, Files, BarChart3, ShieldAlert, LogOut, Menu, ScanLine, User, MapPinned, FilePlus2, Users, Car, UserCog, Building2, Warehouse, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

const NAV = [
  { to: "/albaranes", label: "Albaranes", icon: LayoutList, roles: ["empresa", "gestoria"] },
  { to: "/decas/manual", label: "DeCA manual", icon: FilePlus2, roles: ["empresa", "gestoria"] },
  { to: "/decas", label: "DeCA", icon: Files, roles: ["empresa", "gestoria", "conductor"] },
  { to: "/mapa", label: "Mapa", icon: MapPinned, roles: ["empresa", "gestoria"] },
  { to: "/informes", label: "Informes", icon: BarChart3, roles: ["empresa", "gestoria"] },
  { to: "/cargadores", label: "Cargadores", icon: Warehouse, roles: ["empresa", "gestoria"], group: "Mantenimientos" },
  { to: "/clientes", label: "Clientes", icon: Building2, roles: ["empresa", "gestoria"], group: "Mantenimientos" },
  { to: "/proveedores", label: "Proveedores", icon: Package, roles: ["empresa", "gestoria"], group: "Mantenimientos", ifUser: (u) => !!u.deca_proveedores },
  { to: "/transportistas", label: "Transportistas", icon: Truck, roles: ["empresa", "gestoria"], group: "Mantenimientos" },
  { to: "/conductores", label: "Conductores", icon: Users, roles: ["empresa", "gestoria"], group: "Mantenimientos", ifUser: (u) => !!u.sage_conductores },
  { to: "/matriculas", label: "Matrículas", icon: Car, roles: ["empresa", "gestoria"], group: "Mantenimientos" },
  { to: "/empresas", label: "Empresas", icon: Building2, roles: ["admin"], group: "Administraci\u00f3n" },
  { to: "/usuarios", label: "Usuarios", icon: UserCog, roles: ["admin"], group: "Administraci\u00f3n" },
  { to: "/auditoria", label: "Auditor\u00eda", icon: ShieldAlert, roles: ["admin", "empresa", "gestoria"], group: "Administraci\u00f3n" },
  { to: "/conductor", label: "Conductor", icon: ScanLine, roles: ["conductor"] },
];

function Brand({ to = "/" }) {
  return (
    <Link to={to} className="flex items-center gap-2.5 group">
      <div className="h-9 w-9 rounded-md bg-blue-700 flex items-center justify-center">
        <Truck className="h-5 w-5 text-white" strokeWidth={1.75}/>
      </div>
      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 leading-none">Sistema</div>
        <div className="text-lg font-bold text-slate-900 leading-tight" style={{ fontFamily: "Chivo" }}>DeCA Web</div>
      </div>
    </Link>
  );
}

function SideLinks({ user, onClick }) {
  const location = useLocation();
  const links = NAV.filter((n) => n.roles.includes(user.rol) && (!n.ifUser || n.ifUser(user)));
  const nodos = [];
  let grupo = null;
  for (const n of links) {
    if (n.group && n.group !== grupo) {
      grupo = n.group;
      nodos.push(
        <div
          key={"grupo-" + n.group}
          className={`label-tag px-3 pb-1 ${nodos.length ? "pt-3" : "pt-0"}`}
          data-testid={"nav-grupo-" + String(n.group).toLowerCase()}
        >
          {n.group}
        </div>
      );
    }
    const fueraDeGrupo = !n.group && grupo;
    if (!n.group) grupo = null;
    const Icon = n.icon;
    const active = n.to === "/decas"
      ? location.pathname === "/decas" || /^\/decas\/(?!manual(?:\/|$)|preview(?:\/|$)|generados(?:\/|$))/.test(location.pathname)
      : location.pathname === n.to || location.pathname.startsWith(`${n.to}/`);
    nodos.push(
      <NavLink
        key={n.to}
        to={n.to}
        onClick={onClick}
        data-testid={`nav-${n.to.slice(1)}`}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors tap-44 ${
          fueraDeGrupo ? "mt-2" : ""
        } ${
          active ? "bg-blue-700 text-white" : "text-slate-700 hover:bg-slate-100"
        }`}
      >
        <Icon className="h-4 w-4" strokeWidth={1.75}/>
        <span>{n.label}</span>
      </NavLink>
    );
  }
  return (
    <nav className="flex flex-col gap-0.5">
      {nodos}
    </nav>
  );
}

function UserBadge({ user, onLogout, onCambiarEmpresa }) {
  const grupo = (user?.empresas_grupo || []).filter((e) => e && e.codigo);
  const puedeCambiar = !!user?.multiempresa && grupo.length > 1;
  const activa = user?.empresa_activa || user?.empresa_id || "";
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center">
          <User className="h-4 w-4 text-slate-500" strokeWidth={1.75}/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 truncate">{user.nombre || user.email}</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-[0.08em]">{user.rol}</div>
        </div>
      </div>
      {puedeCambiar ? (
        <label className="block mt-3">
          <span className="text-[10px] uppercase tracking-[0.08em] text-slate-500">Empresa</span>
          <select
            className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-xs font-medium text-slate-800"
            value={activa}
            onChange={(e) => onCambiarEmpresa && onCambiarEmpresa(e.target.value)}
            data-testid="select-empresa-activa"
            aria-label="Cambiar de empresa"
          >
            {grupo.map((e) => (
              <option key={e.codigo} value={e.codigo}>
                {(e.nombre && e.nombre !== e.codigo) ? (e.nombre + " (" + e.codigo + ")") : e.codigo}
              </option>
            ))}
          </select>
        </label>
      ) : (user?.empresa_nombre ? (
        <div className="text-[11px] text-slate-500 mt-2 truncate" title={user.empresa_nombre}>{user.empresa_nombre}</div>
      ) : null)}
      <Button variant="outline" size="sm" onClick={onLogout} data-testid="btn-logout" className="w-full mt-3 text-slate-700">
        <LogOut className="h-3.5 w-3.5 mr-2" strokeWidth={1.75}/> Cerrar sesión
      </Button>
    </div>
  );
}

export default function Shell({ children }) {
  const { user, logout, cambiarEmpresa } = useAuth();
  const nav = useNavigate();
  const homeTo = user?.rol === "admin" ? "/empresas" : "/";
  const esAdmin = user?.rol === "admin";

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const handleCambiarEmpresa = async (codigo) => {
    if (!cambiarEmpresa || !codigo) return;
    try {
      await cambiarEmpresa(codigo);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "No se pudo cambiar de empresa");
    }
  };

  return (
    <div className="deca-shell">
      {/* Topbar (mobile) */}
      <header className="md:hidden sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3">
          <Brand to={homeTo} />
          <div className="flex items-center gap-2">
            {esAdmin ? null : <div data-testid="notif-mobile"><NotificationBell /></div>}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" data-testid="btn-menu-open" aria-label="Abrir menú">
                  <Menu className="h-4 w-4" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-4 flex flex-col h-full overflow-hidden">
                <div className="mb-4 shrink-0"><Brand to={homeTo} /></div>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <SideLinks user={user} onClick={() => document.querySelector('[data-state="open"]')?.click?.()} />
                </div>
                <div className="mt-4 shrink-0">
                  <UserBadge user={user} onLogout={handleLogout} onCambiarEmpresa={handleCambiarEmpresa} />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <div className="md:grid md:grid-cols-[240px_1fr] xl:grid-cols-[260px_1fr] md:items-start">
        {/* Sidebar fijo: empresa y logout siempre visibles */}
        <aside className="hidden md:flex flex-col gap-4 h-dvh sticky top-0 self-start border-r border-slate-200 bg-white p-4 z-20">
          <div className="shrink-0"><Brand to={homeTo} /></div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <SideLinks user={user} />
          </div>
          <div className="shrink-0 pt-1">
            <UserBadge user={user} onLogout={handleLogout} onCambiarEmpresa={handleCambiarEmpresa} />
          </div>
        </aside>

        <main className="min-w-0">
          {/* Topbar tablet+ */}
          <div className="hidden md:flex items-center justify-end gap-3 px-4 sm:px-6 lg:px-8 pt-4">
            {esAdmin ? null : <div data-testid="notif-desktop"><NotificationBell /></div>}
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
